import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import LoadingSpinner from "../../../LoadingSpinner";
import { jobContainerValidation } from "../../../validations/vehicleAssignment";
import ButtonRenderer from "../../../renderer/ButtonRenderer";
import Grid from "../../../Grid";
import SearchDropdown from "../../../input/SearchDropdown";
import Dropdown from "../../../input/Dropdown";
import {
  getFieldValuesByTableAndId,
  getOtherRefSearchDropdown,
  getTextByValueAndKey,
} from "../../../../api/keyvalue";
import Input from "../../../input/Input";
import Button from "../../../input/Button";
import { AUTH_TYPES, FORMS, MODULES } from "../../../../const";
import { isNumber } from "../../../../helper";
import { ResizableScrollingTextRenderer } from "../../../renderer/StyleRenderer";
import DeleteButtonRenderer from "../../../renderer/DeleteButtonRenderer";

const VehicleAssignmentContainer = forwardRef(
  (
    {
      jobId,
      jobCode,
      requestId,
      dropdown,
      isLoading,
      currentValue,
      editMode,
      staId,
      setIsAssignButtonEnabled,
      vceList,
    },
    ref,
  ) => {
    const [jobContainer, setJobContainer] = useState({
      jcoJob: jobId,
      jcoRequest: requestId,
      jcoContainerType: "",
      jcoTrailer: "",
      jcoContainerNumber: "",
      jcoTrailerNumber: "",
      jcoSealNumber1: "",
      jcoSealNumber2: "",
      jcoSealNumber3: "",
      jcoSealNumber4: "",
      jcoStatus: 2,
      jcoActive: 1,
      containerTypeValue: "",
      trailerTypeValue: "",
      trailerValue: "",
    });

    const [currentErrors, setCurrentErrors] = useState({});
    const [isAddLoading, setIsAddLoading] = useState(false);
    const [buttonClicked, setButtonClicked] = useState(false);
    const [deletedList, setDeletedList] = useState([]);
    const [containerCount, setContainerCount] = useState(2);
    const [currentContainer, setCurrentContainer] = useState({});
    const [containerData, setContainerData] = useState([]);
    const [searchFieldReset, setSearchFieldReset] = useState(false);

    useEffect(() => {
      (async () => {
        if (editMode) {
          let currentContainers = [];
          let id = 2;
          for (let value of currentValue) {
            const containerTypeResponse = await getFieldValuesByTableAndId({
              tableName: "masterdata.tms_m_container_type",
              primaryKey: "cot_id",
              fieldNames: "cot_discription",
              ids: value.jcoContainerType,
            });

            const trailerResponse = await getFieldValuesByTableAndId({
              tableName: "masterdata.tms_m_trailers",
              primaryKey: "trl_id",
              fieldNames: "trl_trailor_no",
              ids: value.jcoTrailerNumber,
            });

            const trailerValue =
              trailerResponse && trailerResponse.length > 0
                ? trailerResponse[0].field1
                : "";

            const containerTypeValue = containerTypeResponse[0].field1;
            const trailerTypeValue = await getTextByValueAndKey(
              "trailor_type",
              value.jcoTrailer,
            );

            currentContainers.push({
              ...value,
              id: id,
              containerTypeValue,
              trailerTypeValue,
              trailerValue,
              jcoRequest: value.jcoRequestId,
              jcoJob: value.jcoJobId,
              jcoActive: value.jcoActive ?? 1,
              jcoStatus: value.jcoStatus ?? 2,
            });
            id++;
          }

          setContainerCount(id);
          setContainerData(currentContainers);
        }
      })();
    }, [editMode, currentValue]);

    const handleFieldChange = () => {
      setIsAssignButtonEnabled(true);
    };

    useEffect(() => {
      if (buttonClicked) {
        setJobContainer({
          jcoJob: jobId,
          jcoRequest: requestId,
          jcoContainerType: "",
          jcoTrailer: "",
          jcoContainerNumber: "",
          jcoTrailerNumber: "",
          jcoSealNumber1: "",
          jcoSealNumber2: "",
          jcoSealNumber3: "",
          jcoSealNumber4: "",
          jcoStatus: 2,
          jcoActive: 1,
          containerTypeValue: "",
          trailerTypeValue: "",
          trailerValue: "",
        });
        setSearchFieldReset(true);
        setCurrentContainer({});
        setButtonClicked(false);
      }
    }, [buttonClicked, jobId, requestId]);

    useEffect(() => {
      if (Object.keys(currentContainer).length > 0) {
        setJobContainer({
          ...currentContainer,
          containerTypeValue: currentContainer.containerTypeValue,
          trailerTypeValue: currentContainer.trailerTypeValue,
          trailerValue: currentContainer.trailerValue,
        });
      }
    }, [editMode, currentContainer]);

    useImperativeHandle(ref, () => ({
      getData: () => {
        if (editMode) {
          const updateState = {
            modified: [],
            unmodified: [],
            created: [],
            deleted: deletedList,
          };

          containerData.forEach((container) => {
            if (container.jcoId && container.jcoId !== null) {
              if (container.modified) {
                updateState.modified.push(container);
              } else {
                updateState.unmodified.push(container);
              }
            } else {
              updateState.created.push(container);
            }
          });
          return updateState;
        } else {
          return containerData;
        }
      },
      getSavedContainerIds: () =>
        containerData
          .filter((con) => con.jcoId && con.jcoId !== null)
          .map((jco) => jco.jcoId),
      resetForm: () => {
        setButtonClicked(true);
        setContainerData([]);
        setSearchFieldReset(true);
      },
    }));

    const onAddClick = async () => {
      setIsAddLoading(true);

      const containerErrors = jobContainerValidation(jobContainer);

      if (Object.keys(containerErrors).length === 0) {
        handleFieldChange();
        const containerTypeResponse = await getFieldValuesByTableAndId({
          tableName: "masterdata.tms_m_container_type",
          primaryKey: "cot_id",
          fieldNames: "cot_discription",
          ids: jobContainer.jcoContainerType,
        });

        const containerTypeValue = containerTypeResponse[0].field1;
        const trailerTypeValue = await getTextByValueAndKey(
          "trailor_type",
          jobContainer.jcoTrailer,
        );

        const trailerResponse = await getFieldValuesByTableAndId({
          tableName: "masterdata.tms_m_trailers",
          primaryKey: "trl_id",
          fieldNames: "trl_trailor_no",
          ids: jobContainer.jcoTrailerNumber,
        });

        const trailerValue =
          trailerResponse && trailerResponse.length > 0
            ? trailerResponse[0].field1
            : "";

        if (!jobContainer.jcoId || jobContainer.jcoId === null) {
          if (!jobContainer.id || jobContainer.id === null) {
            setContainerData((prevState) => [
              ...prevState,
              {
                ...jobContainer,
                containerTypeValue,
                trailerTypeValue,
                id: containerCount,
                trailerValue,
              },
            ]);
            setContainerCount(containerCount + 1);
          } else {
            setContainerData((prevState) =>
              prevState.filter(
                (container) => container.id !== jobContainer.id,
              ),
            );

            setContainerData((prevState) => [
              ...prevState,
              {
                ...jobContainer,
                containerTypeValue,
                trailerTypeValue,
                trailerValue,
              },
            ]);
          }
        } else {
          setContainerData((prevState) =>
            prevState.filter((container) => container.id !== jobContainer.id),
          );
          setContainerData((prevState) => [
            ...prevState,
            {
              ...jobContainer,
              containerTypeValue,
              trailerTypeValue,
              trailerValue,
              modified: true,
            },
          ]);
        }
        setButtonClicked(true);
      } else {
        setCurrentErrors(containerErrors);
        document.querySelector(".ui.form").classList.add("error");
      }
      setIsAddLoading(false);
    };

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "70vh" }}
          >
            <div className="ui segment">
              <h4 className="ui dividing header">Types</h4>
              <div className="ui segment">
                <div className="field">
                  <div className={`field`}>
                    <label>Job</label>
                    <input
                      type="text"
                      name="job"
                      placeholder="Job"
                      value={jobCode}
                      disabled
                    />
                  </div>
                </div>
              </div>

              <div className="two fields">
                <Dropdown
                  readOnly={vceList ? !vceList.includes(staId) : true}
                  label="Trailer Type"
                  error={currentErrors.jcoTrailer}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { jcoTrailer: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={dropdown.trailerTypeOpt}
                  style={{ height: "20px" }}
                  placeholder="Trailer Type"
                  value={jobContainer.jcoTrailer}
                  onChange={(e, data) =>
                    setJobContainer((prev) => ({
                      ...prev,
                      jcoTrailer: data.value,
                    }))
                  }
                  module={MODULES.Transaction}
                  form={FORMS.VEHICLE_ASSIGNMENT.CONTAINER.form}
                  field="jcoTrailer"
                  editMode={editMode}
                />
                <SearchDropdown
                  key={1}
                  editMode={editMode}
                  readOnly={vceList ? !vceList.includes(staId) : true}
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  multiGrid={true}
                  multiGridCurrentValue={jobContainer.trailerValue}
                  module={MODULES.Transaction}
                  form={FORMS.VEHICLE_ASSIGNMENT.CONTAINER.form}
                  field="jcoTrailerNumber"
                  onResultSelect={(data) =>
                    setJobContainer((prevState) => ({
                      ...prevState,
                      jcoTrailerNumber: data,
                    }))
                  }
                  api={getOtherRefSearchDropdown}
                  apiDependency={{
                    type: "trailer",
                    value: "",
                  }}
                  threshold={1}
                  clearable={true}
                  fieldInfo={{
                    tableName: "masterdata.tms_m_trailers",
                    primaryKey: "trl_id",
                    fieldName: "trl_trailor_no",
                    ids: jobContainer.jcoTrailerNumber,
                  }}
                  onClear={() => {
                    setJobContainer((prevState) => ({
                      ...prevState,
                      jcoTrailerNumber: "",
                      trailerValue: "",
                    }));
                  }}
                  required={false}
                  label="Trailer"
                  error={currentErrors.jcoTrailerNumber}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { jcoTrailerNumber: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />
              </div>
              <div className="two fields">
                <SearchDropdown
                  key={1}
                  editMode={editMode}
                  readOnly={vceList ? !vceList.includes(staId) : true}
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  multiGrid={true}
                  multiGridCurrentValue={jobContainer.containerTypeValue}
                  module={MODULES.Transaction}
                  form={FORMS.VEHICLE_ASSIGNMENT.CONTAINER.form}
                  field="jcoContainerType"
                  onResultSelect={(data) =>
                    setJobContainer((prevState) => ({
                      ...prevState,
                      jcoContainerType: data,
                    }))
                  }
                  api={getOtherRefSearchDropdown}
                  apiDependency={{
                    type: "container_type",
                    value: "",
                  }}
                  threshold={1}
                  clearable={true}
                  fieldInfo={{
                    tableName: "masterdata.tms_m_container_type",
                    primaryKey: "cot_id",
                    fieldName: "cot_discription",
                    ids: jobContainer.jcoContainerType,
                  }}
                  onClear={() => {
                    setJobContainer((prevState) => ({
                      ...prevState,
                      jcoContainerType: "",
                      containerTypeValue: "",
                    }));
                  }}
                  required={false}
                  label="Container Type"
                  error={currentErrors.jcoContainerType}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { jcoContainerType: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />
                <Input
                  required={true}
                  label="Container Number"
                  readOnly={vceList ? !vceList.includes(staId) : true}
                  error={currentErrors.jcoContainerNumber}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { jcoContainerNumber: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="containerNumber"
                  placeholder="Container Number"
                  value={jobContainer.jcoContainerNumber}
                  module={MODULES.Transaction}
                  form={FORMS.VEHICLE_ASSIGNMENT.CONTAINER.form}
                  field="jcoContainerNumber"
                  editMode={editMode}
                  onChange={(e) =>
                    setJobContainer((prev) => ({
                      ...prev,
                      jcoContainerNumber: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="two fields">
                <Input
                  label="Seal 1"
                  readOnly={vceList ? !vceList.includes(staId) : true}
                  error={currentErrors.jcoSealNumber1}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { jcoSealNumber1: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="seal1"
                  placeholder="Seal 1"
                  value={jobContainer.jcoSealNumber1}
                  onChange={(e) =>
                    setJobContainer((prev) => ({
                      ...prev,
                      jcoSealNumber1: e.target.value,
                    }))
                  }
                  module={MODULES.Transaction}
                  form={FORMS.VEHICLE_ASSIGNMENT.CONTAINER.form}
                  field="jcoSealNumber1"
                  editMode={editMode}
                />
                <Input
                  label="Seal 2"
                  readOnly={vceList ? !vceList.includes(staId) : true}
                  error={currentErrors.jcoSealNumber2}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { jcoSealNumber2: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="seal2"
                  placeholder="Seal 2"
                  value={jobContainer.jcoSealNumber2}
                  onChange={(e) =>
                    setJobContainer((prev) => ({
                      ...prev,
                      jcoSealNumber2: e.target.value,
                    }))
                  }
                  module={MODULES.Transaction}
                  form={FORMS.VEHICLE_ASSIGNMENT.CONTAINER.form}
                  field="jcoSealNumber2"
                  editMode={editMode}
                />
              </div>
              <div className="two fields">
                <Input
                  label="Seal 3"
                  readOnly={vceList ? !vceList.includes(staId) : true}
                  error={currentErrors.jcoSealNumber3}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { jcoSealNumber3: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="seal3"
                  placeholder="Seal 3"
                  value={jobContainer.jcoSealNumber3}
                  onChange={(e) =>
                    setJobContainer((prev) => ({
                      ...prev,
                      jcoSealNumber3: e.target.value,
                    }))
                  }
                  module={MODULES.Transaction}
                  form={FORMS.VEHICLE_ASSIGNMENT.CONTAINER.form}
                  field="jcoSealNumber3"
                  editMode={editMode}
                />

                <Input
                  label="Seal 4"
                  readOnly={vceList ? !vceList.includes(staId) : true}
                  error={currentErrors.jcoSealNumber4}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { jcoSealNumber4: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="seal4"
                  placeholder="Seal 4"
                  value={jobContainer.jcoSealNumber4}
                  onChange={(e) =>
                    setJobContainer((prev) => ({
                      ...prev,
                      jcoSealNumber4: e.target.value,
                    }))
                  }
                  module={MODULES.Transaction}
                  form={FORMS.VEHICLE_ASSIGNMENT.CONTAINER.form}
                  field="jcoSealNumber4"
                  editMode={editMode}
                />
              </div>
{/* 
              <Dropdown
                required={true}
                label="Active"
                readOnly={vceList ? !vceList.includes(staId) : true}
                error={currentErrors.jcoActive}
                authenticated={true}
                needDeletePermission={true}
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { jcoActive: _, ...rest } = prevState;
                    return rest;
                  })
                }
                options={dropdown.activeOpt}
                style={{ height: "20px" }}
                placeholder="Active"
                value={jobContainer.jcoActive}
                onChange={(e, data) =>
                  setJobContainer((prev) => ({
                    ...prev,
                    jcoActive: data.value,
                  }))
                }
                module={MODULES.Transaction}
                form={FORMS.VEHICLE_ASSIGNMENT.CONTAINER.form}
                field="jcoActive"
                editMode={editMode}
              /> */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  template="reset"
                  onClick={(e) => {
                    setButtonClicked(true);
                  }}
                />
                <Button
                  template="addOrSave"
                  create={Object.keys(currentContainer).length === 0}
                  onClick={onAddClick}
                  disabled={vceList ? !vceList.includes(staId) : true}
                  isLoading={isAddLoading}
                  authenticated={true}
                  editMode={editMode}
                  module={MODULES.Transaction}
                  form={FORMS.VEHICLE_ASSIGNMENT.CONTAINER.form}
                />
              </div>
            </div>
            <Grid
              heading="Container List"
              config={[
                {
                  headerName: "Container Type",
                  field: "containerTypeValue",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                { headerName: "Trailer", field: "trailerTypeValue" },
                { headerName: "Container Number", field: "jcoContainerNumber" },
                { headerName: "Trailer Number", field: "trailerValue" },
                { headerName: "Seal 1", field: "jcoSealNumber1" },
                { headerName: "Seal 2", field: "jcoSealNumber2" },
                { headerName: "Seal 3", field: "jcoSealNumber3" },
                { headerName: "Seal 4", field: "jcoSealNumber4" },
                {
                  field: "Edit",
                  filter: true,
                  cellRenderer: ButtonRenderer,
                  cellRendererParams: (params) => ({
                    clickHandler: () => {
                      setCurrentContainer(params.data);
                    },
                    label: "Edit",
                    disabled: vceList ? !vceList.includes(staId) : true,
                    form: FORMS.VEHICLE_ASSIGNMENT.CONTAINER.form,
                    module: MODULES.Transaction,
                    editMode: editMode,
                    consider: AUTH_TYPES.CREATE,
                    authenticated: true,
                  }),
                },
                {
                  field: "Delete",
                  filter: true,
                  cellRenderer: DeleteButtonRenderer,
                  cellRendererParams: (params) => ({
                    consider: AUTH_TYPES.DELETE,
                    clickHandler: () => {
                      handleFieldChange();
                      if (params.data.jcoId && params.data.jcoId !== null) {
                        setDeletedList((prevState) => [
                          ...prevState,
                          params.data.jcoId,
                        ]);
                      }
                      setContainerData((prev) =>
                        prev.filter(
                          (container) => container.id !== params.data.id,
                        ),
                      );
                    },
                    label: "Delete",
                    className: "ui red button",
                    form: FORMS.VEHICLE_ASSIGNMENT.CONTAINER.form,
                    module: MODULES.Transaction,
                    editMode:
                      params.data.jcoId !== null && isNumber(params.data.jcoId),
                    authenticated: true,
                  }),
                },
              ]}
              content={containerData} // content
            />
          </form>
        )}
      </>
    );
  },
);

export default VehicleAssignmentContainer;
